import {
    hasMeaningfulResultContent,
    monetaryAmount,
    PROJECT_AI_ALLOWED_FIELDS,
    PROJECT_AI_IMPACT_QUESTIONS,
    PROJECT_AI_STRATEGIC_OBJECTIVES,
} from './project-ai-draft.mapper.js'

export const AI_OWNERSHIP = Object.freeze({
    MANUAL: 'manual',
    SUGGESTED: 'ai_suggested',
    CONFIRMED: 'confirmed',
    CONTESTED: 'contested',
})

const LOCKED_SKILL_STATES = Object.freeze([
    'understanding',
    'researching',
    'drafting',
    'reviewing',
])

const ENUM_FIELD_VALUES = Object.freeze({
    nivel: ['Estratégico', 'Operacional'],
    finalidade: ['Criação', 'Mudança'],
    area: ['Processo', 'Produto'],
})

export function normalizeAssistantState(state = null) {
    const mode = ['chat', 'skill_running', 'skill_waiting'].includes(state?.mode)
        ? state.mode
        : 'chat'
    const rawActiveSkill = state?.active_skill ?? state?.activeSkill
    const activeSkill = rawActiveSkill === 'project_create'
        ? rawActiveSkill
        : null
    return {
        mode: activeSkill ? mode : 'chat',
        activeSkill,
        activityLabel: typeof (state?.activity_label ?? state?.activityLabel) === 'string'
            ? (state?.activity_label ?? state?.activityLabel)
            : '',
    }
}

export function resolveAssistantPresentation(state = null, pageContext = null, awaitingQuestion = false) {
    const normalized = normalizeAssistantState(state)
    if (normalized.activeSkill === 'project_create') {
        return {
            subtitle: 'Criando proposta de projeto',
            placeholder: awaitingQuestion
                ? 'Responda à pergunta ou faça outro pedido…'
                : 'Descreva o projeto ou complemente as informações…',
            skillLabel: 'Proposta de Projeto',
        }
    }
    const contextual = pageContext?.module === 'projects' && pageContext?.mode === 'create'
        ? 'Projetos • Novo Projeto'
        : null
    return {
        subtitle: contextual || 'Como posso ajudar?',
        placeholder: 'Pergunte ou peça algo ao Thalamus…',
        skillLabel: '',
    }
}

export function isAssistantComposerLocked(skillState, processing = false) {
    void skillState
    void processing
    return false
}

export function shouldShowSkillIndicator(state = null) {
    return normalizeAssistantState(state).activeSkill === 'project_create'
}

export function shouldApplyCommittedDraft(lastAppliedRevision, committedRevision) {
    const next = Number(committedRevision || 0)
    return next > 0 && next > Number(lastAppliedRevision || 0)
}

export function resolveTransientActivity(events = [], skillState = '', processing = false) {
    if (!processing && !LOCKED_SKILL_STATES.includes(String(skillState || ''))) return null
    const fallback = {
        understanding: 'Analisando a iniciativa',
        researching: 'Buscando referências',
        drafting: 'Montando a proposta',
        reviewing: 'Revisando a proposta',
        planning: 'Estruturando o planejamento',
    }[skillState] || ''
    const list = Array.isArray(events) ? events : []
    const cycle = Math.max(1, ...list.map((event) => Number(event?.cycle || 1)))
    const cycleEvents = list.filter((event) => Number(event?.cycle || 1) === cycle)
    const latest = [...cycleEvents].reverse().find((event) =>
        ['started', 'info'].includes(event?.status) || event?.event === 'tool_completed')
        || cycleEvents[cycleEvents.length - 1]
    if (!latest) return fallback ? { label: `${fallback}…`, detail: '' } : null

    let label = latest.label || fallback
    if (!label) return null
    let detail = ''
    const latestResearchResult = [...cycleEvents].reverse()
        .find((event) => event.event === 'research_completed')
    if (latestResearchResult && cycleEvents.indexOf(latestResearchResult) > cycleEvents.indexOf(latest)) {
        detail = latestResearchResult.label || ''
    } else if (latest.event === 'tool_completed' && Number.isFinite(Number(latest.metadata?.result_count))) {
        detail = `${Number(latest.metadata.result_count)} resultado(s) encontrado(s)`
    } else if (latest.status === 'completed' && Number.isFinite(Number(latest.metadata?.result_count))) {
        detail = `${Number(latest.metadata.result_count)} referência(s) relevante(s) encontrada(s)`
    }
    if (!/[….!?]$/.test(label)) label += '…'

    return { label, detail }
}

const RESULT_FIELDS = Object.freeze([
    'nome',
    'problema',
    'descricao',
    'evidencias',
    'dt_prevista_conclusao',
    'custo',
    'sem_custo',
    'cost_state',
    'cost_estimate',
])

function isObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value)
}

export function createLocalId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (token) => {
        const random = Math.floor(Math.random() * 16)
        return (token === 'x' ? random : (random & 0x3) | 0x8).toString(16)
    })
}

export function ensureResultLocalIds(resultados = []) {
    return (Array.isArray(resultados) ? resultados : []).map((resultado) => {
        if (!isObject(resultado)) return resultado
        if (!resultado.local_id) resultado.local_id = createLocalId()
        return resultado
    })
}

export function createAssistantFormState() {
    return {
        contextRevision: 0,
        fieldRevisions: {},
        ownership: {},
        fieldMeta: {},
    }
}

function hasValue(value) {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    return true
}

export function initializeManualOwnership(state, pcm = {}) {
    PROJECT_AI_ALLOWED_FIELDS.forEach((field) => {
        if (['resultados', 'objetivos_estrategicos', 'impacto_viabilidade'].includes(field)) return
        if (hasValue(pcm[field]) && !state.ownership[field]) {
            state.ownership[field] = AI_OWNERSHIP.MANUAL
            state.fieldRevisions[field] = state.contextRevision
        }
    })
    ensureResultLocalIds(pcm.resultados).forEach((resultado) => {
        RESULT_FIELDS.forEach((field) => {
            if (!hasValue(resultado?.[field])) return
            const path = `resultados.${resultado.local_id}.${field}`
            if (!state.ownership[path]) {
                state.ownership[path] = AI_OWNERSHIP.MANUAL
                state.fieldRevisions[path] = state.contextRevision
            }
        })
    })
    ;(Array.isArray(pcm.objetivos_estrategicos) ? pcm.objetivos_estrategicos : [])
        .filter((item) => [true, 1, '1'].includes(item?.ativo))
        .forEach((item) => {
            const id = item.id ?? item.objetivo_id
            if (!id) return
            state.ownership[`objetivos_estrategicos.${id}`] = AI_OWNERSHIP.MANUAL
            state.fieldRevisions[`objetivos_estrategicos.${id}`] = state.contextRevision
            ;['ativo', 'justificativa'].forEach((field) => {
                const path = `objetivos_estrategicos.${id}.${field}`
                state.ownership[path] = AI_OWNERSHIP.MANUAL
                state.fieldRevisions[path] = state.contextRevision
            })
        })
    ;(Array.isArray(pcm.impacto_viabilidade) ? pcm.impacto_viabilidade : [])
        .filter((item) => typeof item?.concordo === 'boolean' || hasValue(item?.justificativa))
        .forEach((item) => {
            const id = item.impactoViabilidade_id
            if (!id) return
            state.ownership[`impacto_viabilidade.${id}`] = AI_OWNERSHIP.MANUAL
            state.fieldRevisions[`impacto_viabilidade.${id}`] = state.contextRevision
            ;['concordo', 'justificativa'].forEach((field) => {
                const path = `impacto_viabilidade.${id}.${field}`
                state.ownership[path] = AI_OWNERSHIP.MANUAL
                state.fieldRevisions[path] = state.contextRevision
            })
        })
    return state
}

export function markFieldManual(state, fieldPath) {
    if (!fieldPath) return state.contextRevision
    state.contextRevision += 1
    state.ownership[fieldPath] = AI_OWNERSHIP.MANUAL
    state.fieldRevisions[fieldPath] = state.contextRevision
    state.fieldMeta[fieldPath] = {
        source: 'user_provided',
        state: 'known',
        resolutionState: 'known',
    }
    return state.contextRevision
}

export function confirmAiFields(state, fieldPaths = null) {
    const selected = fieldPaths ? new Set(fieldPaths) : null
    Object.keys(state.ownership).forEach((path) => {
        if (state.ownership[path] !== AI_OWNERSHIP.SUGGESTED) return
        if (selected && !selected.has(path)) return
        state.ownership[path] = AI_OWNERSHIP.CONFIRMED
    })
}

function isUnresolvedPatchState(state, source, value) {
    if (state === 'missing' || state === 'pending' || source === 'missing') return true
    return value === null || value === undefined
}

function normalizePatch(rawPatch, fallbackRevision) {
    if (!isObject(rawPatch)) return null
    const fieldPath = rawPatch.field_path || rawPatch.fieldPath || rawPatch.path
    if (!fieldPath) return null
    const source = rawPatch.source || 'ai_inferred'
    const resolutionState = rawPatch.resolution_state || rawPatch.resolutionState || null
    const state = resolutionState || rawPatch.state || 'suggested'
    if (isUnresolvedPatchState(state, source, rawPatch.value)) {
        return null
    }
    return {
        fieldPath,
        value: rawPatch.value,
        source,
        state,
        resolutionState,
        requiresConfirmation: rawPatch.requires_confirmation ?? rawPatch.requiresConfirmation ?? true,
        reason: rawPatch.reason || '',
        contextRevision: Number(rawPatch.context_revision ?? rawPatch.contextRevision ?? fallbackRevision ?? 0),
    }
}

export function normalizeAssistantPatches(payload, fallbackRevision = 0) {
    const candidates = payload?.patches
        || payload?.fast_turn?.patches
        || payload?.fast_turn_patch?.patches
        || payload?.deep_pmo?.patches
        || []
    if (Array.isArray(candidates)) {
        return candidates.map((patch) => normalizePatch(patch, fallbackRevision)).filter(Boolean)
    }
    if (isObject(candidates)) {
        return Object.entries(candidates)
            .map(([fieldPath, value]) => normalizePatch({ field_path: fieldPath, value }, fallbackRevision))
            .filter(Boolean)
    }
    return []
}

export function projectDraftToPatches(draft, contextRevision = 0) {
    if (!isObject(draft)) return []
    return PROJECT_AI_ALLOWED_FIELDS.flatMap((fieldPath) => {
        if (!(fieldPath in draft)) return []
        const item = isObject(draft[fieldPath]) && Object.prototype.hasOwnProperty.call(draft[fieldPath], 'value')
            ? draft[fieldPath]
            : { value: draft[fieldPath] }
        const resolutionState = item.resolution_state || item.resolutionState || null
        const state = resolutionState || item.state || 'suggested'
        if (isUnresolvedPatchState(state, item.source, item.value) || item.value === '') return []
        return [{
            field_path: fieldPath,
            value: item.value,
            source: item.source || 'ai_inferred',
            state,
            resolution_state: resolutionState,
            requires_confirmation: item.requires_confirmation ?? true,
            reason: item.reason || '',
            context_revision: contextRevision,
        }]
    })
}

function sameValue(left, right) {
    return JSON.stringify(left) === JSON.stringify(right)
}

function canApplyPath(state, path, patchRevision, currentValue, nextValue) {
    const owner = state.ownership[path]
    const lastManualRevision = Number(state.fieldRevisions[path] || 0)
    if (patchRevision < lastManualRevision) return { allowed: false, reason: 'stale' }
    if ([AI_OWNERSHIP.MANUAL, AI_OWNERSHIP.CONTESTED, AI_OWNERSHIP.CONFIRMED].includes(owner)) {
        if (!sameValue(currentValue, nextValue)) state.ownership[path] = AI_OWNERSHIP.CONTESTED
        return { allowed: false, reason: 'manual' }
    }
    return { allowed: !hasValue(currentValue) || owner === AI_OWNERSHIP.SUGGESTED, reason: 'occupied' }
}

function ownershipForPatch(patch) {
    if (['estimated', 'suggested', 'pending'].includes(patch.state || patch.resolutionState)) {
        return AI_OWNERSHIP.SUGGESTED
    }
    return patch.source === 'user_provided' && patch.requiresConfirmation === false
        ? AI_OWNERSHIP.CONFIRMED
        : AI_OWNERSHIP.SUGGESTED
}

function applyScalarPatch(pcm, state, patch) {
    if (!PROJECT_AI_ALLOWED_FIELDS.includes(patch.fieldPath)) return { status: 'skipped', reason: 'not_allowed' }
    if (ENUM_FIELD_VALUES[patch.fieldPath]
        && !ENUM_FIELD_VALUES[patch.fieldPath].includes(patch.value)
    ) {
        return { status: 'skipped', reason: 'invalid_enum' }
    }
    const decision = canApplyPath(
        state,
        patch.fieldPath,
        patch.contextRevision,
        pcm[patch.fieldPath],
        patch.value,
    )
    if (!decision.allowed) return { status: 'skipped', reason: decision.reason }
    pcm[patch.fieldPath] = patch.value
    state.ownership[patch.fieldPath] = ownershipForPatch(patch)
    state.fieldMeta[patch.fieldPath] = patch
    return { status: 'applied' }
}

function applyResultPatch(pcm, state, patch) {
    if (!Array.isArray(patch.value)) return { status: 'skipped', reason: 'invalid_results' }
    if (!Array.isArray(pcm.resultados)) pcm.resultados = []
    pcm.resultados = pcm.resultados.filter((resultado) => {
        if (hasMeaningfulResultContent(resultado)) return true
        const itemPath = `resultados.${resultado?.local_id}`
        return state.ownership[itemPath] === AI_OWNERSHIP.MANUAL
    })
    ensureResultLocalIds(pcm.resultados)

    let applied = 0
    patch.value.forEach((incoming) => {
        if (!isCompleteAssistantResult(incoming)) return
        const localId = incoming.local_id || createLocalId()
        let current = pcm.resultados.find((item) => item?.local_id === localId)
        const itemPath = `resultados.${localId}`
        if ([AI_OWNERSHIP.MANUAL, AI_OWNERSHIP.CONTESTED, AI_OWNERSHIP.CONFIRMED]
            .includes(state.ownership[itemPath])) return
        if (!current) {
            current = { local_id: localId, status: 'pendente', dt_conclusao: null }
            pcm.resultados.push(current)
        }
        RESULT_FIELDS.forEach((field) => {
            if (!(field in incoming)) return
            const path = `resultados.${localId}.${field}`
            const decision = canApplyPath(
                state,
                path,
                patch.contextRevision,
                current[field],
                incoming[field],
            )
            if (!decision.allowed) return
            current[field] = field === 'custo'
                ? monetaryAmount(incoming[field])
                : incoming[field]
            if (field === 'sem_custo' && incoming[field]) current.custo = null
            state.ownership[path] = ownershipForPatch(patch)
            state.fieldMeta[path] = { ...patch, fieldPath: path }
            applied += 1
        })
    })
    return applied ? { status: 'applied' } : { status: 'skipped', reason: 'protected' }
}

function isCompleteAssistantResult(result) {
    return isObject(result)
        && ['nome', 'problema', 'descricao', 'evidencias']
            .every((field) => typeof result[field] === 'string' && result[field].trim())
}

function canApplyCollectionPath(state, path, patch, currentValue, nextValue) {
    const trackedCurrent = state.ownership[path] ? currentValue : null
    return canApplyPath(state, path, patch.contextRevision, trackedCurrent, nextValue)
}

function applyStrategicObjectivesPatch(pcm, state, patch) {
    if (!Array.isArray(patch.value)) return { status: 'skipped', reason: 'invalid_objectives' }
    if (!Array.isArray(pcm.objetivos_estrategicos) || !pcm.objetivos_estrategicos.length) {
        pcm.objetivos_estrategicos = PROJECT_AI_STRATEGIC_OBJECTIVES.map((item) => ({ ...item, ativo: 0 }))
    }
    let applied = 0
    patch.value.forEach((suggestion) => {
        const id = Number(suggestion?.objetivo_id)
        if (!Number.isInteger(id)) return
        const current = pcm.objetivos_estrategicos.find((item) => Number(item.id ?? item.objetivo_id) === id)
        if (!current) return
        if ([AI_OWNERSHIP.MANUAL, AI_OWNERSHIP.CONTESTED, AI_OWNERSHIP.CONFIRMED]
            .includes(state.ownership[`objetivos_estrategicos.${id}`])) return
        const values = {
            ativo: 1,
            justificativa: String(suggestion.justificativa || '').trim(),
        }
        Object.entries(values).forEach(([field, nextValue]) => {
            const path = `objetivos_estrategicos.${id}.${field}`
            const decision = canApplyCollectionPath(state, path, patch, current[field], nextValue)
            if (!decision.allowed) return
            current[field] = nextValue
            state.ownership[path] = ownershipForPatch(patch)
            state.fieldMeta[path] = { ...patch, fieldPath: path }
            applied += 1
        })
    })
    return applied ? { status: 'applied' } : { status: 'skipped', reason: 'protected' }
}

function applyImpactAnalysisPatch(pcm, state, patch) {
    if (!Array.isArray(patch.value)) return { status: 'skipped', reason: 'invalid_impact' }
    if (!Array.isArray(pcm.impacto_viabilidade) || !pcm.impacto_viabilidade.length) {
        pcm.impacto_viabilidade = PROJECT_AI_IMPACT_QUESTIONS.map((item) => ({
            ...item,
            concordo: null,
            justificativa: '',
        }))
    }
    let applied = 0
    patch.value.forEach((suggestion) => {
        const id = Number(suggestion?.impactoViabilidade_id)
        if (!Number.isInteger(id)) return
        const current = pcm.impacto_viabilidade.find((item) => Number(item.impactoViabilidade_id) === id)
        if (!current) return
        if ([AI_OWNERSHIP.MANUAL, AI_OWNERSHIP.CONTESTED, AI_OWNERSHIP.CONFIRMED]
            .includes(state.ownership[`impacto_viabilidade.${id}`])) return
        const values = {
            concordo: typeof suggestion.concordo === 'boolean' ? suggestion.concordo : null,
            justificativa: String(
                suggestion.justificativa || suggestion.reason || suggestion.impact_reason || '',
            ).trim(),
            impact_state: ['yes', 'no', 'pending'].includes(suggestion.impact_state)
                ? suggestion.impact_state
                : null,
            impact_reason: String(suggestion.reason || suggestion.impact_reason || '').trim(),
            impact_confidence: typeof suggestion.confidence === 'number'
                ? suggestion.confidence
                : null,
            impact_requires_confirmation: suggestion.requires_confirmation === true,
        }
        Object.entries(values).forEach(([field, nextValue]) => {
            const path = `impacto_viabilidade.${id}.${field}`
            const decision = canApplyCollectionPath(state, path, patch, current[field], nextValue)
            if (!decision.allowed) return
            current[field] = nextValue
            state.ownership[path] = ownershipForPatch(patch)
            state.fieldMeta[path] = { ...patch, fieldPath: path }
            applied += 1
        })
    })
    return applied ? { status: 'applied' } : { status: 'skipped', reason: 'protected' }
}

export function applyAssistantPatches(pcm, state, rawPatches = []) {
    const report = { applied: [], skipped: [] }
    normalizeAssistantPatches({ patches: rawPatches }, state.contextRevision).forEach((patch) => {
        const result = patch.fieldPath === 'resultados'
            ? applyResultPatch(pcm, state, patch)
            : patch.fieldPath === 'objetivos_estrategicos'
                ? applyStrategicObjectivesPatch(pcm, state, patch)
                : patch.fieldPath === 'impacto_viabilidade'
                    ? applyImpactAnalysisPatch(pcm, state, patch)
            : applyScalarPatch(pcm, state, patch)
        report[result.status].push({ field_path: patch.fieldPath, reason: result.reason })
    })
    return report
}

export function sanitizeProjectContext(pcm = {}, state = createAssistantFormState()) {
    const formState = {}
    PROJECT_AI_ALLOWED_FIELDS.forEach((field) => {
        if (field === 'resultados') {
            const value = ensureResultLocalIds(pcm.resultados)
                .filter(hasMeaningfulResultContent)
                .map((resultado) => {
                const safe = { local_id: resultado.local_id }
                RESULT_FIELDS.forEach((key) => {
                    if (resultado[key] !== undefined) safe[key] = resultado[key]
                })
                return safe
            })
            const resultPaths = Object.keys(state.ownership).filter((path) => path.startsWith('resultados.'))
            const resultOwners = resultPaths.map((path) => state.ownership[path])
            if (!value.length && !resultPaths.length) return
            formState.resultados = {
                value,
                ownership: resultOwners.includes(AI_OWNERSHIP.MANUAL)
                    ? AI_OWNERSHIP.MANUAL
                    : (resultOwners[0] || AI_OWNERSHIP.MANUAL),
                field_revision: Math.max(0, ...resultPaths.map((path) => state.fieldRevisions[path] || 0)),
            }
            return
        }
        if (field === 'objetivos_estrategicos') {
            const value = (Array.isArray(pcm.objetivos_estrategicos) ? pcm.objetivos_estrategicos : [])
                .filter((item) => [true, 1, '1'].includes(item?.ativo))
                .map((item) => ({
                    objetivo_id: Number(item.id ?? item.objetivo_id),
                    justificativa: item.justificativa || '',
                }))
                .filter((item) => Number.isInteger(item.objetivo_id))
            const entry = collectionContextEntry(field, value, state)
            if (entry) formState[field] = entry
            return
        }
        if (field === 'impacto_viabilidade') {
            const value = (Array.isArray(pcm.impacto_viabilidade) ? pcm.impacto_viabilidade : [])
                .filter((item) => Number.isInteger(Number(item?.impactoViabilidade_id)))
                .filter((item) => typeof item.concordo === 'boolean'
                    || hasValue(item.justificativa)
                    || ['yes', 'no', 'pending'].includes(item.impact_state)
                    || hasValue(item.impact_reason))
                .map((item) => ({
                    impactoViabilidade_id: Number(item.impactoViabilidade_id),
                    concordo: typeof item.concordo === 'boolean' ? item.concordo : null,
                    justificativa: item.justificativa || '',
                    impact_state: ['yes', 'no', 'pending'].includes(item.impact_state)
                        ? item.impact_state
                        : null,
                    reason: item.impact_reason || '',
                    confidence: typeof item.impact_confidence === 'number'
                        ? item.impact_confidence
                        : null,
                    requires_confirmation: item.impact_requires_confirmation === true,
                }))
            const entry = collectionContextEntry(field, value, state)
            if (entry) formState[field] = entry
            return
        }
        if (hasValue(pcm[field]) || state.ownership[field]) {
            formState[field] = {
                value: pcm[field],
                ownership: state.ownership[field] || AI_OWNERSHIP.MANUAL,
                field_revision: state.fieldRevisions[field] || 0,
            }
        }
    })
    return {
        version: 1,
        context_revision: state.contextRevision,
        form_state: formState,
    }
}

function collectionContextEntry(field, value, state) {
    const paths = Object.keys(state.ownership).filter((path) => path.startsWith(`${field}.`))
    if (!value.length && !paths.length) return null
    const owners = paths.map((path) => state.ownership[path])
    return {
        value,
        ownership: owners.includes(AI_OWNERSHIP.MANUAL)
            ? AI_OWNERSHIP.MANUAL
            : (owners[0] || AI_OWNERSHIP.MANUAL),
        field_revision: Math.max(0, ...paths.map((path) => state.fieldRevisions[path] || 0)),
    }
}
